import { Box, Grid, Typography } from '@mui/material';
import { UUID } from 'crypto';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createTaskAttachment } from 'services/product-task/createTaskAttachment';
import { deleteTaskAttachment } from 'services/product-task/deleteTaskAttachment';
import { AppDispatch, RootState } from 'store';
import { getSingleServiceAgreement } from 'thunks/accountServiceAgreementsThunk';
import DeleteDocumentIcon from 'assets/icons/delete-document-icon.svg?react';
import ViewDocumentIcon from 'assets/icons/view-document-icon.svg?react';
import PDFIcon from 'assets/icons/pdf-icon.svg?react';
import { getTaskAttachment } from 'services/product-task/getTaskAttachment';
import { JBFileDropZone } from '@bjb-gcrm/gcrm-frontend-components';
import { LoadingSpinner } from 'components/loadingSpinner/LoadingSpinner';
import { closeModal, openModal } from 'reducers/modalReducer';

export const FileUploadWidget = () => {
  const [isLoading, setIsLoading] = React.useState(false);
  const openedTaskUid = useSelector((state: RootState) => state.serviceAgreements.openedTaskUid);

  const dispatch = useDispatch<AppDispatch>();
  const { selectedServiceAgreement } = useSelector((state: RootState) => state.serviceAgreements);

  const currentTaskAttachments =
    selectedServiceAgreement?.tasks?.find((task) => task.taskUid === openedTaskUid)?.attachments ?? [];

  const isTaskInProgress =
    selectedServiceAgreement?.tasks?.find((task) => task.taskUid === openedTaskUid)?.resolution === 'In progress';

  const taskIdFromStepMetadata = selectedServiceAgreement?.stepMetadata?.find(
    (stepMetadata) => stepMetadata.techId === openedTaskUid,
  )?.taskId;

  const handleCreateAttachment = async (file: File | null) => {
    if (!file) {
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'No valid file was uploaded',
              userErrorMessage: 'An error occurred when uploading file. Please try again later.',
            },
          },
        }),
      );
      return;
    }

    try {
      setIsLoading(true);
      if (!selectedServiceAgreement?.serviceAgreementId || !taskIdFromStepMetadata) {
        return;
      }
      const res = await createTaskAttachment(taskIdFromStepMetadata, selectedServiceAgreement.serviceAgreementId, file);
      if (res) {
        dispatch(getSingleServiceAgreement(selectedServiceAgreement?.serviceAgreementId, null));
      }
    } catch (error: unknown) {
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when creating attachment for task',
              userErrorMessage: 'An error occurred when creating attachment for task. Please try again later.',
            },
          },
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadAttachment = async (taskAttachmentId: UUID) => {
    try {
      setIsLoading(true);
      if (!selectedServiceAgreement?.serviceAgreementId) {
        return;
      }
      const taskAttachment = await getTaskAttachment(taskAttachmentId, selectedServiceAgreement.serviceAgreementId);

      const fileName = currentTaskAttachments.find(
        (attachment) => attachment.taskAttachmentId === taskAttachmentId,
      )?.taskAttachmentName;

      if (taskAttachment) {
        const fileURL = window.URL.createObjectURL(taskAttachment);

        const fileLink = document.createElement('a');
        fileLink.href = fileURL;

        fileLink.setAttribute('download', fileName ?? 'download.pdf');
        document.body.appendChild(fileLink);
        fileLink.click();
        document.body.removeChild(fileLink);
        window.URL.revokeObjectURL(fileURL);
      }
    } catch (error: unknown) {
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when downloading attachment of task',
              userErrorMessage: 'An error occurred when downloading attachment of task. Please try again later.',
            },
          },
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAttachment = async (taskAttachmentId: UUID) => {
    try {
      setIsLoading(true);
      if (!selectedServiceAgreement?.serviceAgreementId) {
        return;
      }
      await deleteTaskAttachment(taskAttachmentId, selectedServiceAgreement.serviceAgreementId);
      dispatch(getSingleServiceAgreement(selectedServiceAgreement?.serviceAgreementId, null));
    } catch (error: unknown) {
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when deleting attachment of task',
              userErrorMessage: 'An error occurred when deleting attachment of task. Please try again later.',
            },
          },
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Grid container spacing={2} sx={{ height: '500px', mt: 1, width: '100%' }}>
      <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
        {isTaskInProgress &&
          (!isLoading ? (
            <>
              <Typography variant="h2" sx={{ mb: 1 }}>
                Attach documents
              </Typography>
              <JBFileDropZone handleFileChange={handleCreateAttachment} fileInput={null} />
            </>
          ) : (
            <LoadingSpinner message={'Loading documents...'} />
          ))}
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '200px',
          overflowY: 'auto',
        }}
      >
        <Typography variant="h2" sx={{ mb: 2 }}>
          Attached documents
        </Typography>
        {currentTaskAttachments?.map((attachment, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PDFIcon style={{ marginRight: 4 }} />
            <Typography
              variant="body1"
              sx={{
                flexGrow: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '250px',
              }}
              title={attachment.taskAttachmentName}
            >
              {attachment.taskAttachmentName}
            </Typography>
            <ViewDocumentIcon
              style={{ marginRight: 10, cursor: 'pointer' }}
              onClick={() => handleDownloadAttachment((attachment.taskAttachmentId ?? '') as UUID)}
            />
            {isTaskInProgress && (
              <DeleteDocumentIcon
                style={{ marginLeft: 1, cursor: 'pointer', color: 'pink' }}
                onClick={() => handleDeleteAttachment((attachment.taskAttachmentId ?? '') as UUID)}
              />
            )}
          </Box>
        ))}
      </Grid>
    </Grid>
  );
};
